import style from './style.module.scss'
import { useContext, useEffect } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { Header, Icon, Input, Stack } from '@crossfit/ui'
import { Button } from '@crossfit/design-system'
import Form from 'components/Admin/Shared/Form'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { yupResolver } from '@hookform/resolvers/yup'
import { patchAffiliate } from 'services/affiliates'
import { useFormNotification } from 'hooks/useFormNotification'
import ErrorWrapper from 'components/Admin/Shared/Form/ErrorWrapper'
import { WebsiteProps, schema } from './schema'
import { FormData } from './schema'

export const WebsiteForm = (props: WebsiteProps) => {
  const { setIsEditing } = props
  const isMobile = useMediaQuery('(max-width: 900px)')
  const handleFormNotification = useFormNotification()

  const {
    register,
    reset,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(schema) as unknown as Resolver<FormData>,
  })

  const cancel = (e) => {
    e.preventDefault()
    setIsEditing(false)
  }

  const onSubmit = handleSubmit(async ({ website }) => {
    try {
      const patchRequest = await patchAffiliate(props.affiliateId, {
        web: website,
      })

      if (patchRequest.status < 300) {
        props.update(website)
        reset({ website })
        handleFormNotification('success')
        setIsEditing(false)
      }
    } catch (error) {
      if (error.request.status === 400) {
        handleFormNotification('error', {
          message: 'The website you supplied is invalid',
        })
        setError('website', {
          type: 'manual',
          message: 'Please provide a valid url',
        })
      } else {
        const errorDetail =
          JSON.parse(error?.request?.response ?? {})?.detail ??
          'Something went wrong'
        handleFormNotification('error', { message: errorDetail })
      }
    }
  })
  const web = watch('website')
  /*
  1st Capturing Group (https?://)
    http matches the characters http literally (case sensitive)
    s matches the character s with index 11510 (7316 or 1638) literally (case sensitive)
      ? matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
    : matches the character : with index 5810 (3A16 or 728) literally (case sensitive)
    / matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
    / matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
  2nd Capturing Group (.+)
    . matches any character (except for line terminators)
    + matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
  3rd Capturing Group (\\..+)
    \\. matches the character . with index 4610 (2E16 or 568) literally (case sensitive)
    . matches any character (except for line terminators)
    + matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
  */
  const validUrl = web && new RegExp('(https?://)(.+)(\\..+)').test(web)

  return (
    <div className={style.wrapper}>
      <Form id={'dkjbe'} className={style.formWrapper}>
        {isMobile && (
          <div className={style.formHeader}>
            <Icon
              name='chevron'
              className={style.chevron}
              height={16}
              width={16}
              onClick={(e) => cancel(e)}
            />
            <Header as='h3' size='small' className={style.header}>
              {'Website'}
            </Header>
          </div>
        )}
        <Stack
          className={style.formInputs}
          direction={['horizontal', 'vertical']}
          spacing={6}
        >
          <Stack direction='vertical' className={style.formInput}>
            <ErrorWrapper
              showError={!!errors?.website}
              errorMessage={errors?.website?.message}
            >
              <Input
                id='simple-form-input-1'
                defaultValue={props.defaultValue}
                disabled={false}
                required={true}
                {...register('website', { required: true, maxLength: 100 })}
              />
            </ErrorWrapper>
          </Stack>
        </Stack>
        <div className={style.formButtons}>
          <Button
            variant='secondary'
            className={style.cancelButton}
            onClick={(e) => cancel(e)}
            size='sm'
          >
            Cancel
          </Button>
          <Button
            size='sm'
            className={style.saveButton}
            onClick={onSubmit}
            variant='primary'
            disabled={!validUrl}
          >
            Save
          </Button>
        </div>
      </Form>
    </div>
  )
}
